import React, { useState, useRef, useEffect } from 'react';

export default function IndustryCodeReturnDropdown({
  options = [],
  value = '',
  onChange,
  id = 'industryCodeReturn',
  name = 'industryCodeReturn',
  placeholder = 'Select Industry Code'
}) {
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Remove duplicate entries based on nicCode (keep first occurrence)
  const uniqueOptions = React.useMemo(() => {
    const seen = new Set();
    return options.filter((option) => {
      const key = String(option.nicCode);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [options]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return uniqueOptions;
    }
    const searchLower = searchTerm.toLowerCase();
    return uniqueOptions.filter((option) => {
      const codeMatch = String(option.nicCode).toLowerCase().includes(searchLower);
      const descMatch = String(option.nicDescription || '').toLowerCase().includes(searchLower);
      return codeMatch || descMatch;
    });
  }, [uniqueOptions, searchTerm]);

  const selectedOption = uniqueOptions.find(
    (option) => String(option.nicCode) === String(value)
  );

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      // Focus search input when dropdown opens
      if (newState && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } else if (!newState) {
        // Clear search when dropdown closes
        setSearchTerm('');
      }
      return newState;
    });
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleOptionSelect = (option) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: option ? option.nicCode : ''
        }
      });
    }
    closeDropdown();
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={`status-unit-dropdown ${isOpen ? 'open' : ''}`}
      ref={dropdownRef}
      id={`${id}-wrapper`}
    >
      <button
        type="button"
        className="form-select status-unit-dropdown__trigger"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        id={id}
      >
        <span className="status-unit-dropdown__selected">
          {selectedOption
            ? `${selectedOption.nicCode} - ${selectedOption.nicDescription}`
            : placeholder}
        </span>
        <span className="status-unit-dropdown__chevron">
          <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true" />
        </span>
      </button>

      {isOpen && (
        <ul
          className="status-unit-dropdown__list"
          role="listbox"
          aria-activedescendant={selectedOption ? `${id}-${selectedOption.nicCode}` : undefined}
        >
          {/* Search Input */}
          <li className="status-unit-dropdown__search" role="none">
            <input
              ref={searchInputRef}
              type="text"
              className="form-control"
              placeholder="Search by code or description..."
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  closeDropdown();
                } else if (e.key === 'Enter' && filteredOptions.length === 1) {
                  handleOptionSelect(filteredOptions[0]);
                }
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '4px'
              }}
            />
          </li>
          <li
            role="option"
            aria-selected={!selectedOption}
            className={`status-unit-dropdown__item ${!selectedOption ? 'selected' : ''}`}
            onClick={() => handleOptionSelect(null)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleOptionSelect(null);
              }
            }}
            tabIndex={0}
          >
            {placeholder}
          </li>
          {filteredOptions.length === 0 ? (
            <li className="status-unit-dropdown__item" style={{ color: '#999', fontStyle: 'italic' }}>
              No results found
            </li>
          ) : (
            filteredOptions.map((option, index) => (
            <li
              key={`${id}-${option.nicCode}-${index}`}
              id={`${id}-${option.nicCode}-${index}`}
              role="option"
              aria-selected={String(option.nicCode) === String(value)}
              className={`status-unit-dropdown__item ${
                String(option.nicCode) === String(value) ? 'selected' : ''
              }`}
              onClick={() => handleOptionSelect(option)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleOptionSelect(option);
                }
              }}
              tabIndex={0}
            >
              <span className="status-unit-dropdown__item-code">{option.nicCode}</span>
              <span className="status-unit-dropdown__item-description">
                {option.nicDescription}
              </span>
            </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

